import { useState } from 'react';
import type { FormEvent } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { DataTable } from '@/components/DataTable';
import { useClients, useCreateClient } from '@/hooks/useDesk';

export function ClientsPage() {
  const [q, setQ] = useState('');
  const { data, isLoading, isError, error } = useClients(q || undefined);
  const create = useCreateClient();
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await create.mutateAsync({
      company_name: company,
      contact_name: contact || null,
      email: email || null,
      phone: phone || null,
    });
    setCompany('');
    setContact('');
    setEmail('');
    setPhone('');
    setOpen(false);
  }

  return (
    <div>
      <PageHeader
        title="Clients"
        description="Organisations and contacts for the desk."
        actions={
          <button
            type="button"
            className="rounded-lg bg-dome-forest px-3 py-2 text-sm font-medium text-dome-sand"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Close' : 'New client'}
          </button>
        }
      />

      <div className="mb-4">
        <input
          className="w-full max-w-md rounded-lg border border-[var(--border)] bg-white/80 px-3 py-2 text-sm"
          placeholder="Search clients…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {open ? (
        <form
          onSubmit={onSubmit}
          className="mb-5 grid gap-3 rounded-xl border border-[var(--border)] bg-white/80 p-4 sm:grid-cols-2"
        >
          <label className="text-sm">
            <span className="mb-1 block font-medium">Company</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              required
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Contact</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Email</span>
            <input
              type="email"
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Phone</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-lg bg-dome-brass px-4 py-2 text-sm font-medium text-dome-ink"
            >
              {create.isPending ? 'Saving…' : 'Save client'}
            </button>
          </div>
        </form>
      ) : null}

      {isLoading ? (
        <p className="animate-pulse-soft text-sm text-[var(--muted)]">Loading…</p>
      ) : null}
      {isError ? (
        <p className="text-sm text-dome-danger">{(error as Error).message}</p>
      ) : null}
      {!isLoading && data && data.length === 0 ? (
        <EmptyState
          title="No clients yet"
          body="Clients are created here or automatically from Quick quote."
        />
      ) : null}
      {data && data.length > 0 ? (
        <DataTable headers={['Company', 'Contact', 'Email', 'Phone', 'Cimso']}>
          {data.map((row) => (
            <tr key={row.id}>
              <td className="px-3 py-2 font-medium">{row.company_name}</td>
              <td className="px-3 py-2">{row.contact_name || '—'}</td>
              <td className="px-3 py-2">{row.email || '—'}</td>
              <td className="px-3 py-2">{row.phone || '—'}</td>
              <td className="px-3 py-2 font-mono text-xs">
                {row.cimso_client_id ?? '—'}
              </td>
            </tr>
          ))}
        </DataTable>
      ) : null}
    </div>
  );
}
